import React, { useState, useEffect } from 'react';
import { axiosInstance as axios } from '../../context/AuthContext';
import { FileText, Shield, Upload, Users, History, AlertTriangle } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';
import UpgradeModal from '../UpgradeModal';

import { API } from './vault/vaultConstants';
import { DocumentsTab } from './vault/DocumentsTab';
import { ExecutorsTab } from './vault/ExecutorsTab';
import { ActivityLogTab } from './vault/ActivityLogTab';
import { EmergencyAccessTab } from './vault/EmergencyAccessTab';
import { UploadDocumentModal } from './vault/UploadDocumentModal';
import { EditDocumentModal } from './vault/EditDocumentModal';
import { AddExecutorModal } from './vault/AddExecutorModal';
import { EmergencyRequestModal } from './vault/EmergencyRequestModal';
import { VaultSecurityModal } from './vault/VaultSecurityModal';

export function VaultTab({ familyId, currentUser, isOwner, familyMembers = [] }) {
  const { tier } = useSubscription();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [executors, setExecutors] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [myRole, setMyRole] = useState({ role: 'member', is_owner: false, is_executor: false });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('documents');
  const [showSecurityModal, setShowSecurityModal] = useState(false);

  // Emergency access states
  const [emergencyRequests, setEmergencyRequests] = useState([]);
  const [myEmergencyRequest, setMyEmergencyRequest] = useState(null);
  const [hasEmergencyAccess, setHasEmergencyAccess] = useState(false);
  const [showEmergencyRequestModal, setShowEmergencyRequestModal] = useState(false);
  const [emergencyReason, setEmergencyReason] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [processingRequestId, setProcessingRequestId] = useState(null);

  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddExecutorModal, setShowAddExecutorModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentTypes, setDocumentTypes] = useState([]);

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    title: '',
    document_type: 'other',
    description: '',
    visibility: 'executors',
    file: null
  });

  const hasVaultAccess = tier === 'growing' || tier === 'flourishing';

  useEffect(() => {
    if (hasVaultAccess) {
      fetchAll();
    } else {
      setLoading(false);
    }
  }, [familyId, hasVaultAccess]);

  const fetchAll = async () => {
    try {
      const requests = [
        axios.get(`${API}/vault/families/${familyId}/documents`, { withCredentials: true }),
        axios.get(`${API}/vault/families/${familyId}/executors`, { withCredentials: true }),
        axios.get(`${API}/vault/families/${familyId}/my-role`, { withCredentials: true }),
        axios.get(`${API}/vault/document-types`, { withCredentials: true }),
        axios.get(`${API}/vault/emergency/${familyId}/my-request`, { withCredentials: true })
      ];

      if (isOwner) {
        requests.push(axios.get(`${API}/vault/families/${familyId}/audit-logs?limit=50`, { withCredentials: true }));
        requests.push(axios.get(`${API}/vault/emergency/${familyId}/requests`, { withCredentials: true }));
      }

      const results = await Promise.all(requests);
      setDocuments(results[0].data.documents || []);
      setExecutors(results[1].data.executors || []);
      setMyRole(results[2].data);
      setDocumentTypes(results[3].data.types || []);
      setMyEmergencyRequest(results[4].data.request);
      setHasEmergencyAccess(results[4].data.has_emergency_access || false);

      if (isOwner) {
        if (results[5]) setAuditLogs(results[5].data.audit_logs || []);
        if (results[6]) setEmergencyRequests(results[6].data.requests || []);
      }
    } catch (error) {
      console.error('Error fetching vault data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 25 * 1024 * 1024) {
        alert('File size exceeds 25MB limit');
        return;
      }
      setUploadForm(prev => ({ ...prev, file }));
    }
  };

  const resetUploadForm = () =>
    setUploadForm({ title: '', document_type: 'other', description: '', visibility: 'executors', file: null });

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.title.trim()) {
      alert('Please provide a title and select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('family_id', familyId);
      formData.append('title', uploadForm.title.trim());
      formData.append('document_type', uploadForm.document_type);
      formData.append('visibility', uploadForm.visibility);
      if (uploadForm.description.trim()) {
        formData.append('description', uploadForm.description.trim());
      }

      await axios.post(`${API}/vault/documents`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setShowUploadModal(false);
      resetUploadForm();
      fetchAll();
    } catch (error) {
      alert(error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/vault/documents/${doc.document_id}/file`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      let message = 'Failed to download document';
      try {
        if (error.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          message = JSON.parse(text).detail || message;
        } else {
          message = error.response?.data?.detail || message;
        }
      } catch (_) { /* keep generic message */ }
      alert(message);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Are you sure you want to delete "${doc.title}"? This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/vault/documents/${doc.document_id}`, { withCredentials: true });
      fetchAll();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedDocument) return;
    try {
      await axios.put(`${API}/vault/documents/${selectedDocument.document_id}`, null, {
        params: {
          title: selectedDocument.title,
          document_type: selectedDocument.document_type,
          description: selectedDocument.description || '',
          visibility: selectedDocument.visibility
        },
        withCredentials: true
      });
      setShowEditModal(false);
      setSelectedDocument(null);
      fetchAll();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update document');
    }
  };

  const handleAddExecutor = async (memberId) => {
    try {
      await axios.post(`${API}/vault/families/${familyId}/executors?executor_id=${memberId}`, {}, { withCredentials: true });
      setShowAddExecutorModal(false);
      fetchAll();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add executor');
    }
  };

  const handleRemoveExecutor = async (executorId) => {
    if (!window.confirm('Remove this executor? They will no longer have access to restricted documents.')) return;
    try {
      await axios.delete(`${API}/vault/families/${familyId}/executors/${executorId}`, { withCredentials: true });
      fetchAll();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to remove executor');
    }
  };

  // Emergency Access Handlers
  const handleRequestEmergencyAccess = async (e) => {
    e.preventDefault();
    if (!emergencyReason.trim()) {
      alert('Please provide a reason for your emergency access request');
      return;
    }
    setSubmittingRequest(true);
    try {
      await axios.post(`${API}/vault/emergency/${familyId}/request`, { reason: emergencyReason.trim() }, { withCredentials: true });
      setShowEmergencyRequestModal(false);
      setEmergencyReason('');
      fetchAll();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to submit emergency access request');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const handleApproveRequest = async (requestId) => {
    if (!window.confirm('Approve this emergency access request? The user will gain full access to executor-level documents.')) return;
    setProcessingRequestId(requestId);
    try {
      await axios.post(`${API}/vault/emergency/${familyId}/requests/${requestId}/approve`, {}, { withCredentials: true });
      fetchAll();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessingRequestId(null);
    }
  };

  const handleDenyRequest = async (requestId) => {
    const note = window.prompt('Optional: Provide a reason for denying this request');
    setProcessingRequestId(requestId);
    try {
      await axios.post(`${API}/vault/emergency/${familyId}/requests/${requestId}/deny`, {}, {
        params: note ? { note } : {},
        withCredentials: true
      });
      fetchAll();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to deny request');
    } finally {
      setProcessingRequestId(null);
    }
  };

  const handleRevokeAccess = async (requestId) => {
    if (!window.confirm('Revoke this emergency access? The user will lose their elevated access to vault documents.')) return;
    setProcessingRequestId(requestId);
    try {
      await axios.post(`${API}/vault/emergency/${familyId}/requests/${requestId}/revoke`, {}, { withCredentials: true });
      fetchAll();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to revoke access');
    } finally {
      setProcessingRequestId(null);
    }
  };

  const pendingRequestsCount = emergencyRequests.filter(r => r.status === 'pending').length;
  const canModify = (doc) => doc.uploaded_by === currentUser?.user_id || isOwner;

  // Available members for executor assignment
  const availableMembers = familyMembers.filter(m =>
    !executors.some(e => e.executor_id === m.user_id) &&
    m.user_id !== currentUser?.user_id
  );

  if (loading) {
    return (
      <div className="text-center py-12" data-testid="vault-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading vault...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vault-tab">
      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} feature="legal_vault" requiredTier="growing" />

      {/* Banner */}
      <div className="relative rounded-xl overflow-hidden shadow-sm ring-1 ring-black/5" data-testid="vault-banner">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/26ab570e-45c9-45ce-a09e-336bb6c38467/images/74bc98086aeb5614cdc8b5bcb10858499d0c41e0e4a391587480cdfcd35cbd17.jpeg"
          alt="Family legal documents labeled Insurance, Will, Trust and Deed being placed securely inside a steel vault"
          className="w-full h-auto"
          loading="lazy"
        />
        <button
          onClick={() => setShowSecurityModal(true)}
          className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 bg-white/90 backdrop-blur-sm text-teal-700 text-xs sm:text-sm font-medium rounded-full shadow-md hover:bg-white transition-colors"
          data-testid="vault-security-info-btn"
        >
          <Shield className="w-4 h-4" />
          How your vault stays secure
        </button>
      </div>

      {!hasVaultAccess ? (
        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl border border-teal-200 p-8 text-center" data-testid="vault-locked">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-teal-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Legal Vault</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Securely store important family documents with access controls. Available on Growing plan and above.
          </p>
          <button onClick={() => setShowUpgradeModal(true)} className="px-6 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors" data-testid="vault-upgrade-btn">
            Upgrade to Growing
          </button>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Shield className="w-5 h-5 text-teal-600" />
                Legal Vault
              </h2>
              <p className="text-gray-600 text-sm mt-1">
                {myRole.role === 'owner' ? 'You are the vault owner' : myRole.role === 'executor' ? 'You are a designated executor' : 'Family member access'}
              </p>
            </div>
            <button onClick={() => setShowUploadModal(true)} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors" data-testid="vault-upload-btn">
              <Upload className="w-4 h-4" />
              Upload Document
            </button>
          </div>

          {/* Sub-tabs */}
          <div className="flex space-x-1 border-b overflow-x-auto">
            <button onClick={() => setActiveSubTab('documents')} className={`px-4 py-2 font-medium text-sm rounded-t-lg whitespace-nowrap ${activeSubTab === 'documents' ? 'bg-teal-50 text-teal-700 border-b-2 border-teal-600' : 'text-gray-600 hover:text-gray-800'}`}>
              <FileText className="w-4 h-4 inline mr-2" />Documents ({documents.length})
            </button>
            {isOwner && (
              <>
                <button onClick={() => setActiveSubTab('executors')} className={`px-4 py-2 font-medium text-sm rounded-t-lg whitespace-nowrap ${activeSubTab === 'executors' ? 'bg-teal-50 text-teal-700 border-b-2 border-teal-600' : 'text-gray-600 hover:text-gray-800'}`}>
                  <Users className="w-4 h-4 inline mr-2" />Executors ({executors.length})
                </button>
                <button onClick={() => setActiveSubTab('emergency')} className={`px-4 py-2 font-medium text-sm rounded-t-lg whitespace-nowrap relative ${activeSubTab === 'emergency' ? 'bg-red-50 text-red-700 border-b-2 border-red-600' : 'text-gray-600 hover:text-gray-800'}`} data-testid="emergency-access-tab">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />Emergency Access
                  {pendingRequestsCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {pendingRequestsCount}
                    </span>
                  )}
                </button>
                <button onClick={() => setActiveSubTab('activity')} className={`px-4 py-2 font-medium text-sm rounded-t-lg whitespace-nowrap ${activeSubTab === 'activity' ? 'bg-teal-50 text-teal-700 border-b-2 border-teal-600' : 'text-gray-600 hover:text-gray-800'}`} data-testid="activity-log-tab">
                  <History className="w-4 h-4 inline mr-2" />Activity Log
                </button>
              </>
            )}
            {!isOwner && myRole.is_executor && !hasEmergencyAccess && (
              <button onClick={() => setActiveSubTab('emergency')} className={`px-4 py-2 font-medium text-sm rounded-t-lg whitespace-nowrap ${activeSubTab === 'emergency' ? 'bg-red-50 text-red-700 border-b-2 border-red-600' : 'text-gray-600 hover:text-gray-800'}`}>
                <AlertTriangle className="w-4 h-4 inline mr-2" />Emergency Access
              </button>
            )}
          </div>

          {activeSubTab === 'documents' && (
            <DocumentsTab
              documents={documents}
              canModify={canModify}
              onUploadClick={() => setShowUploadModal(true)}
              onEdit={(doc) => { setSelectedDocument({ ...doc }); setShowEditModal(true); }}
              onDelete={handleDelete}
              onDownload={handleDownload}
            />
          )}

          {activeSubTab === 'executors' && isOwner && (
            <ExecutorsTab
              executors={executors}
              onAddClick={() => setShowAddExecutorModal(true)}
              onRemove={handleRemoveExecutor}
            />
          )}

          {activeSubTab === 'activity' && isOwner && <ActivityLogTab auditLogs={auditLogs} />}

          {activeSubTab === 'emergency' && (
            <EmergencyAccessTab
              isOwner={isOwner}
              emergencyRequests={emergencyRequests}
              myEmergencyRequest={myEmergencyRequest}
              processingRequestId={processingRequestId}
              onApprove={handleApproveRequest}
              onDeny={handleDenyRequest}
              onRevoke={handleRevokeAccess}
              onRequestClick={() => setShowEmergencyRequestModal(true)}
            />
          )}
        </>
      )}

      {showUploadModal && (
        <UploadDocumentModal
          uploadForm={uploadForm}
          setUploadForm={setUploadForm}
          documentTypes={documentTypes}
          uploading={uploading}
          onSubmit={handleUpload}
          onClose={() => { setShowUploadModal(false); resetUploadForm(); }}
          onFileChange={handleFileChange}
        />
      )}

      {showEditModal && selectedDocument && (
        <EditDocumentModal
          selectedDocument={selectedDocument}
          setSelectedDocument={setSelectedDocument}
          documentTypes={documentTypes}
          onSubmit={handleUpdate}
          onClose={() => { setShowEditModal(false); setSelectedDocument(null); }}
        />
      )}

      {showAddExecutorModal && (
        <AddExecutorModal
          availableMembers={availableMembers}
          onAdd={handleAddExecutor}
          onClose={() => setShowAddExecutorModal(false)}
        />
      )}

      {showEmergencyRequestModal && (
        <EmergencyRequestModal
          emergencyReason={emergencyReason}
          setEmergencyReason={setEmergencyReason}
          submittingRequest={submittingRequest}
          onSubmit={handleRequestEmergencyAccess}
          onClose={() => { setShowEmergencyRequestModal(false); setEmergencyReason(''); }}
        />
      )}
      {showSecurityModal && (
        <VaultSecurityModal onClose={() => setShowSecurityModal(false)} />
      )}
    </div>
  );
}
